/**
 * Tabs beneath the scratchpad displaying information about the scratchpad,
 * as well as tabs for transcripts, spin-offs, and documentation.
 */
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {color} from "@khanacademy/wonder-blocks-tokens";

import * as sharedStyles from "#content-library-legacy/styles/shared.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import {deprecatedTypography} from "#shared-styles-package/deprecated-typography.ts";

export type Tab = {
    content: React.ReactNode;
    title: string;
};

type Props = {
    // The tabs of content to show
    tabs: Array<Tab>;
};

type State = {
    currentTabIndex: number;
};

class ScratchpadTabs extends React.Component<Props, State> {
    static defaultProps: {
        tabs: Array<any>;
    } = {
        tabs: [],
    };

    state: State = {
        currentTabIndex: 0,
    };

    onTabChange: (e: React.SyntheticEvent, tabIndex: number) => void = (
        e: React.SyntheticEvent,
        tabIndex: number,
    ) => {
        e.preventDefault();
        this.setState({currentTabIndex: tabIndex});
    };

    render(): null | React.ReactElement<React.ComponentProps<"div">> {
        const tabs = this.props.tabs.filter((tab) => tab.content);

        if (!tabs.length) {
            return null;
        }

        return (
            <div className={css(styles.info)}>
                <div
                    role="tablist"
                    className={css(styles.tabRow)}
                    id="scratchpad-tabs"
                >
                    {tabs.map((tab, i) => {
                        const isSelected = i === this.state.currentTabIndex;
                        return (
                            <View
                                key={i}
                                role="tab"
                                aria-selected={isSelected ? "true" : "false"}
                                style={[
                                    styles.tabTrigger,
                                    isSelected
                                        ? styles.activeTab
                                        : styles.inactiveTab,
                                ]}
                                onClick={(e: React.SyntheticEvent) =>
                                    this.onTabChange(e, i)
                                }
                            >
                                {tab.title}
                            </View>
                        );
                    })}
                </div>
                <div className={css(styles.separator)} />
                <div className={css(styles.tabContent)}>
                    {tabs[this.state.currentTabIndex].content}
                </div>
            </div>
        );
    }
}

const styles = StyleSheet.create({
    info: {
        maxWidth: 688,
        margin: "0 auto",
    },

    activeTab: {
        color: color.blue,
        borderBottom: `5px solid ${color.blue}`,
    },

    inactiveTab: {
        ":hover": {
            borderBottom: "5px solid #E3E5E6",
            transition: "border-width 75ms ease-in-out",
        },
        borderBottom: "0px solid #E3E5E6",
        transition: "border-width 150ms ease-in-out",
    },

    tabContent: {
        ...sharedStyles.mixins.padding(16, 16, 16, 16),
        ...deprecatedTypography.bodySmall,
        color: color.offBlack,
    },

    tabRow: {
        display: "flex",
        justifyContent: "center",
    },

    tabTrigger: {
        ...sharedStyles.mixins.padding(14, 17, 8, 17),
        display: "block",
        fontFamily: "inherit",
        fontSize: 17,
        fontWeight: "bold",
        lineHeight: "21px",
        zIndex: 1,
        cursor: "pointer",

        ":hover": {
            textDecoration: "none",
        },
    },

    separator: {
        backgroundColor: color.offBlack16,
        height: 1,
        position: "relative",
        top: -1,
    },
});

export default ScratchpadTabs;
